/* テキスト */
import React, { memo, useCallback, useState } from 'react';
import { FormControl, Col, Row } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import Messages from '../molecules/Messages';
import Paragraph from './Paragraph';
import { convert_types, mask_string, link_setting } from '../Const';
import { convertData } from '../Convert';
import { NumericLiteral } from 'typescript';
import Link from './Link';

interface Props {
  id: string;
  fm_id?: number;
  css_class?: string;
  label: string;
  type?: string; // デフォルトはテキスト
  need?: boolean;
  value: string;
  placeholder: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  message?: string;
  name?: string;
  suffix?: string;
  prefix?: string;
  help_msg?: string;
  disabled: boolean;
  maxlength?: number;
  handleBlur?: VoidFunction;
  handleFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  add_comma?: boolean;
  input_assistance?: string;
  heading?: string;
  right_padding?: boolean;
  looks_inactive?: boolean;
  readonly?: boolean;
  show?: boolean;
  display_border?: boolean;
}

const Text: React.FC<Props> = ({
  id,
  fm_id,
  css_class,
  label,
  type,
  need,
  value,
  placeholder,
  handleChange,
  message,
  name,
  suffix,
  prefix,
  help_msg,
  disabled,
  maxlength,
  handleBlur,
  handleFocus,
  add_comma,
  input_assistance,
  heading,
  right_padding = false,
  looks_inactive,
  readonly,
  show,
  display_border,
}) => {
  const [is_init, setIsInit] = useState(true);

  const returnTextFormJsx = () => {
    /**
     * フォーカスイベント
     * @param event イベントオブジェクト
     */
    const handleFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
      // 1回目のフォーカスで初期表示フラグを倒す
      if (is_init) {
        setIsInit(false);
      }
      // props.handleFocusがundefinedでなければメソッドを実行する
      if (typeof handleFocus !== 'undefined') {
        handleFocus(event);
      }
    };

    /**
     * 値をセットする
     * @returns value 値（初期表示時はコンバート後の文字列）
     */
    const setValue = () => {
      // input_assistanceがセットされていて、初期表示の時はデータコンバートを行う
      if (typeof input_assistance !== 'undefined' && is_init) {
        return convertData(
          // input_assistanceがrepayment_simulationの場合、addCommaが動作するようにする
          input_assistance &&
            (input_assistance === link_setting.input_assistance_name.repayment_simulation_housing ||
              input_assistance === link_setting.input_assistance_name.repayment_simulation_car)
            ? convert_types.add_comma
            : input_assistance,
          value
        );
      }
      return value;
    };

    // リンクの表示
    const displayLink = () => {
      if (
        input_assistance &&
        (input_assistance === link_setting.input_assistance_name.repayment_simulation_housing ||
          input_assistance === link_setting.input_assistance_name.repayment_simulation_car)
      ) {
        return (
          <Link
            display_name={link_setting.repayment_simulation.name}
            anchor={
              input_assistance === link_setting.input_assistance_name.repayment_simulation_housing
                ? link_setting.repayment_simulation.anchor_housing
                : link_setting.repayment_simulation.anchor_car
            }
            color="link-green"
            bold={true}
            css_class="link-icon-newtab"
          />
        );
      }
    };

    /**
     * テキストボックスを描画する
     * @return JSX.Element テキストボックスのElement
     */
    const displayFormControl = () => {
      return typeof message === 'undefined' || message.length === 0 ? (
        <div className={`${right_padding ? 'text-padding-right' : ''}`}>
          <FormControl
            id={id}
            data-fm_id={fm_id}
            type={type}
            placeholder={placeholder}
            value={setValue()}
            onChange={handleChange}
            name={name}
            disabled={disabled || readonly}
            onBlur={handleBlur}
            onFocus={handleFocusInput}
            maxLength={maxlength}
          />
        </div>
      ) : (
        <div className={`${right_padding ? 'text-padding-right' : ''}`}>
          <input
            className="form-control textarea-error"
            id={id}
            data-fm_id={fm_id}
            type={type}
            placeholder={placeholder}
            value={setValue()}
            onChange={handleChange}
            name={name}
            disabled={disabled || readonly}
            onBlur={handleBlur}
            onFocus={handleFocusInput}
            maxLength={maxlength}
          />
        </div>
      );
    };

    // prefixの値を表示する
    const displayPrefix = () => {
      return <div className={`ml-3 text-mt  ${css_class ?? ''}`}>{prefix}</div>;
    };

    // suffixの値を表示する
    const displaySuffix = () => {
      return (
        <Col md={2} lg={2} xl={2} className={`text-mt px-0 suffix  ${css_class ?? ''}`}>
          {suffix}
        </Col>
      );
    };

    return suffix || prefix ? (
      <>
        <h4
          className={`
          heading4
          ${typeof heading === 'undefined' ? 'heading4-disabled' : ''}
        `}
        >
          {heading}
        </h4>
        {displayLink()}
        <Row className="sp-suffix-row">
          {prefix && displayPrefix()}
          <Col className={`sp-suffix-col ${css_class ?? ''}`}>{displayFormControl()}</Col>
          {suffix && displaySuffix()}
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
      </>
    ) : (
      <>
        <h4
          className={`
          heading4
          ${typeof heading === 'undefined' ? 'heading4-disabled' : ''}
        `}
        >
          {heading}
        </h4>
        {displayLink()}
        <div className="sp-text">{displayFormControl()}</div>
        <Messages help_msg={help_msg} valid_msg={message} />
      </>
    );
  };

  /**
   * ラベルを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnLabelJsx = () => {
    let new_value = value;
    if (typeof input_assistance !== 'undefined') {
      new_value = convertData(
        input_assistance &&
          (input_assistance === link_setting.input_assistance_name.repayment_simulation_housing ||
            input_assistance === link_setting.input_assistance_name.repayment_simulation_car)
          ? convert_types.add_comma
          : input_assistance,
        value
      );
    }

    // prefixとsuffixの値の有無で、ラベル表示を切り替える
    const switchDisplayPrefixSuffixParagraph = () => {
      if (prefix && suffix) {
        return <Paragraph label={new_value ? `${prefix}${new_value}${suffix}` : ''} />;
      } else if (prefix) {
        return <Paragraph label={new_value ? `${prefix}${new_value}` : ''} />;
      } else if (suffix) {
        return <Paragraph label={new_value ? `${new_value}${suffix}` : ''} />;
      } else {
        return <Paragraph label={new_value ? new_value : ''} />;
      }
    };

    return (
      <Row>
        <Col>{switchDisplayPrefixSuffixParagraph()}</Col>
      </Row>
    );
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      show={show}
      content={disabled || looks_inactive ? returnLabelJsx() : returnTextFormJsx()}
      display_border={display_border}
    />
  );
};

export default memo(Text);
